import { Map, Spinner } from 'app/assets/icons'
import { ButtonSmall, ResponsiveContainer } from 'app/assets/styles'
import { Link } from 'app/common/link'
import { fonts, SmallText } from 'app/common/typography'
import { Restaurant } from 'app/store-locator/restaurant'
import React, { Component } from 'react'
import { FormattedMessage } from 'react-intl'
import { colors } from 'shared/assets/styles'
import styled, { css } from 'styled-components'
import type { EventMetadata } from 'shared/store/metadata'
import type { SearchParams } from 'shared/store/store-locator'
import type { RestaurantShortDTO } from 'shared/types'
import type { IntlShape } from 'react-intl'
import { InputAddressAutocomplete } from 'app/store-locator/input-address-autocomplete'
import { removeLocalitiesContainerFromDOM } from 'app/common/utils'

type Props = {
    restaurants: ?Array<RestaurantShortDTO>,
    address: string,
    loading: boolean,
    open: boolean,
    search: SearchParams => void,
    setLoading: boolean => void,
    handleClose: () => void,
    handleOnQuickSearchChange: (searchValue?: string) => void,
    clearSearch: () => void,
    selectRestaurant: RestaurantShortDTO => void,
    sendMeta: EventMetadata => void,
    navigate: (to: string) => void,
    geocodingError: boolean => void,
    onMouseEnter: MouseEvent => void,
    onMouseLeave: MouseEvent => void,
    numberOfResults: number,
    moreResults: boolean,
    resultsTitleKey: string,
    intl: IntlShape
}

type State = {
    address: string,
    suggestion: any
}

class StoreQuickSearchComponent extends Component<Props, State> {
    input: HTMLInputElement
    searchOptions: any
    storeLocatorRoute: string

    constructor(props: Props) {
        super()
        const { address, intl } = props
        this.state = {
            address: address || '',
            suggestion: undefined
        }
        this.storeLocatorRoute = intl.formatMessage({
            id: 'routes.restaurants'
        })
        this.searchOptions = {
            componentRestrictions: {
                country: ['fr']
            }
        }
    }

    componentDidUpdate() {
        this.handleStoreQuickSearchMouseEvents()
    }

    handleStoreQuickSearchMouseEvents = () => {
        const localitiesContainer = document.querySelector(
            '.localities-container'
        )

        const sqsComponnent = document.querySelector(
            '.store-quick-search-component'
        )

        if (localitiesContainer) {
            localitiesContainer.addEventListener('click', () => {
                localitiesContainer.remove()
            })

            localitiesContainer.addEventListener('mouseleave', () => {
                !sqsComponnent && localitiesContainer.remove()
            })
        }
    }

    selectRestaurant = (r: RestaurantShortDTO) => {
        this.props.selectRestaurant(r)
        removeLocalitiesContainerFromDOM()
        this.props.navigate(this.storeLocatorRoute)
    }

    clear = () => {
        this.setState({
            address: ''
        })
        this.props.clearSearch()
        this.props.handleClose()
    }

    setSuggestion = (suggest: any) => {
        this.setState({
            suggestion: suggest
        })
    }

    render() {
        const {
            restaurants,
            loading,
            numberOfResults,
            moreResults,
            resultsTitleKey,
            handleOnQuickSearchChange
        } = this.props
        const { suggestion } = this.state
        const placeholder = 'storelocator.search.placeholder.desktop'

        return (
            <div className="store-quick-search-component">
                <InputAddressAutocomplete
                    handleOnQuickSearchChange={handleOnQuickSearchChange}
                    handleOnSuggestionChanged={this.setSuggestion}
                    placeholder={placeholder}
                    forQuickSearch
                />
                {suggestion && (
                    <RestaurantsResultsContainer className="restaurants-results">
                        {!loading && !!suggestion && restaurants && (
                            <ResponsiveContainer>
                                <Results>
                                    <FormattedMessage
                                        id={`storelocator.results.title.${resultsTitleKey}`}
                                        values={{
                                            number: numberOfResults
                                        }}>
                                        {(text: string) => (
                                            <ResultsTitle
                                                moreResults={moreResults}>
                                                {text}
                                            </ResultsTitle>
                                        )}
                                    </FormattedMessage>
                                    {moreResults && (
                                        <FormattedMessage id="storelocator.search.results.see">
                                            {(text: string) => (
                                                <SeeAllResults
                                                    url={
                                                        this.storeLocatorRoute
                                                    }>
                                                    {text}
                                                </SeeAllResults>
                                            )}
                                        </FormattedMessage>
                                    )}
                                </Results>
                                {restaurants.map(
                                    (r: RestaurantShortDTO, index) => (
                                        <Restaurant
                                            key={index}
                                            restaurant={r}
                                            active={false}
                                            minified
                                            onSelectRestaurant={() =>
                                                this.selectRestaurant(r)
                                            }
                                        />
                                    )
                                )}
                            </ResponsiveContainer>
                        )}
                    </RestaurantsResultsContainer>
                )}
            </div>
        )
    }
}

const RestaurantsResultsContainer = styled.div`
    background: white;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
`
const Results = styled.div`
    padding: 20px 0 10px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
`

const ResultsTitle = styled(SmallText)`
    display: block;
    color: ${colors.grey};
    text-transform: uppercase;
    ${props => {
        if (props.moreResults)
            return `
            &:after {
                content: '−';
                padding: 0 10px;
            }
        `
    }};
`

const SeeAllResults = styled(Link)`
    color: black;
    font-family: ${fonts.flameRegular}
    font-size: 14px;
    line-height: 20px;
    text-decoration: underline;
    text-transform: uppercase;
    &:hover {
        color: black;
    }
`

export { StoreQuickSearchComponent }
